import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import { ConfirmationModalShell } from '@/shared/components/confirmation'
import { confirmationBackdropSx } from '@/shared/components/confirmation/confirmationModalStyles'
import { RiCloseLine } from '@/shared/icons/remix'

interface Props {
  open: boolean
  onConfirm: () => void
  /** Close the whole eKYC flow (not back to Verify prompt). */
  onClose: () => void
  isStarting: boolean
}

/** Balanced prerequisites dialog — matches EkycHomePrompt scale. */
const MODAL_WIDTH = 480
const PRIMARY = '#008CD0'
const TITLE = '#0D1B2A'
const BODY = '#62666F'
const CLOSE_BG = '#F4F7FA'
const WARNING = '#B45309'

export function EkycPrerequisitesDialog({
  open,
  onConfirm,
  onClose,
  isStarting,
}: Readonly<Props>) {
  return (
    <ConfirmationModalShell
      open={open}
      onClose={(_event, reason) => {
        if (isStarting) return
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') onClose()
      }}
      dialogTestId="ekyc-prerequisites-dialog"
      ariaLabelledBy="ekyc-prerequisites-title"
      ariaDescribedBy="ekyc-prerequisites-body"
      backdropSx={confirmationBackdropSx}
      paperSx={{
        width: { xs: 'calc(100% - 32px)', sm: MODAL_WIDTH },
        maxWidth: MODAL_WIDTH,
        borderRadius: '14px',
        boxShadow: '0px 8px 20px rgba(0, 0, 0, 0.18)',
        bgcolor: '#FFFFFF',
      }}
    >
      <Box sx={{ boxSizing: 'border-box', width: '100%', px: '24px', pt: '22px', pb: '22px' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 1.5,
            mb: '12px',
          }}
        >
          <Typography
            id="ekyc-prerequisites-title"
            component="h2"
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontWeight: 700,
              fontSize: '18px',
              lineHeight: '24px',
              color: TITLE,
              pr: 1,
              m: 0,
            }}
          >
            Before You Proceed with Identity Verification
          </Typography>
          <IconButton
            aria-label="Close"
            onClick={onClose}
            disabled={isStarting}
            data-testid="ekyc-prerequisites-close"
            disableRipple
            sx={{
              width: 30,
              height: 30,
              borderRadius: '8px',
              bgcolor: CLOSE_BG,
              color: TITLE,
              p: 0,
              flexShrink: 0,
              '&:hover': { bgcolor: '#E8EEF3' },
            }}
          >
            <RiCloseLine size={20} aria-hidden />
          </IconButton>
        </Box>

        <Box id="ekyc-prerequisites-body" sx={{ mb: '28px' }}>
          <Typography
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '20px',
              color: BODY,
              mb: '12px',
            }}
          >
            Please make sure you have the following ready:
          </Typography>

          <Box
            component="ol"
            sx={{
              m: 0,
              pl: '20px',
              mb: '14px',
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
            }}
          >
            <Typography
              component="li"
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '20px',
                color: TITLE,
              }}
            >
              A valid government-issued ID.
            </Typography>
            <Typography
              component="li"
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '20px',
                color: TITLE,
              }}
            >
              Proof of your residential address.
            </Typography>
          </Box>

          <Box
            sx={{
              bgcolor: '#FFF7ED',
              border: '1px solid #FED7AA',
              borderRadius: '8px',
              px: '12px',
              py: '10px',
            }}
          >
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontWeight: 600,
                fontSize: '13px',
                lineHeight: '18px',
                color: WARNING,
              }}
            >
              Important:{' '}
              <Box component="span" sx={{ fontWeight: 500 }}>
                Ensure that the address you provide matches both your government-issued ID and your
                proof of address document to avoid suspension.
              </Box>
            </Typography>
          </Box>
        </Box>

        <Button
          fullWidth
          variant="contained"
          onClick={onConfirm}
          disabled={isStarting}
          data-testid="ekyc-prerequisites-confirm"
          sx={{
            textTransform: 'none',
            bgcolor: PRIMARY,
            color: '#FFFFFF',
            borderRadius: '8px',
            height: 48,
            minHeight: 48,
            py: 0,
            fontFamily: '"Inter", sans-serif',
            fontWeight: 600,
            fontSize: '16px',
            lineHeight: '24px',
            boxShadow: '0px 6px 12px -3px #BAE6FD, 0px 2px 4px -2px #BAE6FD',
            '&:hover': {
              bgcolor: '#0077B3',
              boxShadow: '0px 6px 12px -3px #BAE6FD, 0px 2px 4px -2px #BAE6FD',
            },
            '&.Mui-disabled': { opacity: 0.7 },
          }}
        >
          {isStarting ? <CircularProgress size={20} color="inherit" /> : 'I Understand'}
        </Button>
      </Box>
    </ConfirmationModalShell>
  )
}
